'use client';

import { useCallback } from 'react';
import {
    ADDED_TO_BASKET_MODAL_ID,
    Locale,
    Position,
    PRODUCT_INFO_QUICK_VIEW_MENU_ID,
    PRODUCT_NOTIFY_ME,
} from '@headless-workspace/config';
import { SideMenuModal, useUIContext } from '@headless-workspace/core-ui';
import { NotifyMeModal, QuickViewProductMenu } from '../../../client';
import type { VipBrandsValue } from '../../../server';
import { OpenModalParams, ProductModalHelpers } from '../productDetails/infos/selector/ProductModalHelpers';

export type UseProductModalDisplayReturnType = {
    openQuickViewModal: (productSku: string, vipBrands: VipBrandsValue, locale: Locale) => void;
    openNotifyMeModal: (productId: string, locale: Locale) => void;
    displayModal: (params: OpenModalParams) => void;
    isBasketModalOpen: boolean;
};

export const useProductModalDisplay = (): UseProductModalDisplayReturnType => {
    const {
        menuModal: { addIfAbsentAndOpen, close: closeMenuModal, sideMenuModalList },
    } = useUIContext();

    const isBasketModalOpen = sideMenuModalList[ADDED_TO_BASKET_MODAL_ID]?.isOpen ?? false;

    const openQuickViewModal = useCallback(
        (productSku: string, vipBrands: VipBrandsValue, locale: Locale) => {
            const content = <QuickViewProductMenu productSku={productSku} vipBrands={vipBrands} locale={locale} />;
            addIfAbsentAndOpen(
                PRODUCT_INFO_QUICK_VIEW_MENU_ID,
                SideMenuModal(Position.Right, { default: content }, 'flex flex-col overflow-hidden bg-background-l0'),
            );
        },
        [addIfAbsentAndOpen],
    );

    const openNotifyMeModal = useCallback(
        (productId: string, locale: Locale) => {
            const content = <NotifyMeModal locale={locale} productIds={[productId]} />;
            addIfAbsentAndOpen(PRODUCT_NOTIFY_ME, SideMenuModal(Position.Right, { default: content }));
        },
        [addIfAbsentAndOpen],
    );

    const displayModal = useCallback(
        (params: OpenModalParams) => {
            const modalInfo = ProductModalHelpers.createModalOptions(params);
            if (modalInfo) {
                addIfAbsentAndOpen(modalInfo.modalId, modalInfo.modal);
            } else {
                closeMenuModal(ADDED_TO_BASKET_MODAL_ID, true);
            }
        },
        [addIfAbsentAndOpen, closeMenuModal],
    );

    return {
        openQuickViewModal,
        openNotifyMeModal,
        displayModal,
        isBasketModalOpen,
    };
};
